import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";

const LightContext = createContext(null);

/**
 * Global lighting state.
 * - `activated`: has the user clicked the switch? (persists once true)
 * - `hasTouch`: coarse pointer / no-hover device; spotlight follows scroll instead of cursor
 * - Cursor coords are written as CSS custom properties on <html> for zero-rerender perf.
 */
export function LightProvider({ children }) {
    const [activated, setActivated] = useState(false);
    const [hasTouch, setHasTouch] = useState(false);
    const [theme, setTheme] = useState("gold"); // 'gold' | 'green'
    const rafRef = useRef(null);
    const targetRef = useRef({ x: 0, y: 0 });
    const currentRef = useRef({ x: 0, y: 0 });

    // Detect touch / no-hover once
    useEffect(() => {
        if (typeof window === "undefined") return;
        const mq = window.matchMedia("(hover: none), (pointer: coarse)");
        const set = () => setHasTouch(mq.matches);
        set();
        mq.addEventListener?.("change", set);
        return () => mq.removeEventListener?.("change", set);
    }, []);

    // Initial spotlight anchor: center of viewport
    useEffect(() => {
        if (typeof window === "undefined") return;
        const cx = window.innerWidth / 2;
        const cy = window.innerHeight / 2;
        targetRef.current = { x: cx, y: cy };
        currentRef.current = { x: cx, y: cy };
        document.documentElement.style.setProperty("--sx", `${cx}px`);
        document.documentElement.style.setProperty("--sy", `${cy}px`);
    }, []);

    // rAF loop that smooths cursor position via cheap linear interpolation
    useEffect(() => {
        if (!activated) return;

        const step = () => {
            const t = targetRef.current;
            const c = currentRef.current;
            // ease toward target
            c.x += (t.x - c.x) * 0.18;
            c.y += (t.y - c.y) * 0.18;
            document.documentElement.style.setProperty("--sx", `${c.x}px`);
            document.documentElement.style.setProperty("--sy", `${c.y}px`);
            rafRef.current = requestAnimationFrame(step);
        };
        rafRef.current = requestAnimationFrame(step);
        return () => cancelAnimationFrame(rafRef.current);
    }, [activated]);

    // Mouse tracking (desktop / hover-capable)
    useEffect(() => {
        if (!activated || hasTouch) return;
        const onMove = (e) => {
            targetRef.current = { x: e.clientX, y: e.clientY };
        };
        window.addEventListener("pointermove", onMove, { passive: true });
        return () => window.removeEventListener("pointermove", onMove);
    }, [activated, hasTouch]);

    // Touch fallback: anchor spotlight around the vertical center of the viewport
    // (which is naturally the section the user is scrolling into view).
    useEffect(() => {
        if (!activated || !hasTouch) return;
        const setToViewportCenter = () => {
            const cx = window.innerWidth / 2;
            const cy = window.innerHeight / 2;
            targetRef.current = { x: cx, y: cy };
        };
        setToViewportCenter();
        window.addEventListener("scroll", setToViewportCenter, { passive: true });
        window.addEventListener("resize", setToViewportCenter);
        return () => {
            window.removeEventListener("scroll", setToViewportCenter);
            window.removeEventListener("resize", setToViewportCenter);
        };
    }, [activated, hasTouch]);

    // Public API — apply the "lit" flag to <html> so CSS can react site-wide
    useEffect(() => {
        const el = document.documentElement;
        if (activated) el.setAttribute("data-lit", "true");
        else el.removeAttribute("data-lit");
        return () => el.removeAttribute("data-lit");
    }, [activated]);

    const activate = useCallback((originX, originY) => {
        if (originX != null && originY != null) {
            targetRef.current = { x: originX, y: originY };
            currentRef.current = { x: originX, y: originY };
            document.documentElement.style.setProperty("--sx", `${originX}px`);
            document.documentElement.style.setProperty("--sy", `${originY}px`);
        }
        setActivated(true);
    }, []);

    // Reverse the lights — used when stepping through the portal door
    const deactivate = useCallback(() => {
        setActivated(false);
    }, []);

    // Emit theme onto <html> so index.css can swap palette + spotlight tint
    useEffect(() => {
        const el = document.documentElement;
        if (theme && theme !== "gold") el.setAttribute("data-theme", theme);
        else el.removeAttribute("data-theme");
        return () => el.removeAttribute("data-theme");
    }, [theme]);

    return (
        <LightContext.Provider
            value={{ activated, hasTouch, theme, activate, deactivate, setTheme }}
        >
            {children}
        </LightContext.Provider>
    );
}

export const useLight = () => {
    const ctx = useContext(LightContext);
    if (!ctx) throw new Error("useLight must be used inside LightProvider");
    return ctx;
};
